import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { SearchableSelect } from '../ui/SearchableSelect';
import { AlertMessage } from '../ui/AlertMessage';
import { useToast } from '../ui/Toast';
import { ModuleAccessBanner } from '../ui/ModuleAccessBanner';
import { usePermissions } from '../../permissions/PermissionContext';
import { api, extractErrorMessage } from '../../api/client';
import {
  CHALLAN_PARTY_TYPES,
  GSTIN_PATTERN,
  MAX_CHALLAN_ROWS,
  challanEditPath,
  challansListPath,
  downloadChallanPdf,
  formatMeters,
  normalizeGstNo,
  todayIso,
} from './challanUtils';
import ChallanPreviewModal from './ChallanPreviewModal';

/*
 * Takas are a plain table (like Excel): every cell shows exactly what will be saved and printed.
 * Help for fast entry:
 *  - "Add rows": new rows get the next Taka No. and copy Saree + Design from the row above.
 *  - "Bulk update": choose rows (e.g. 1 to 10, or click Sr. then Shift+click) and set the
 *    Design / Saree for all of them at once.
 *  - Enter moves down in the same column; Enter on the last row adds a row.
 *  - Pasting a column (or several columns) from Excel / WhatsApp fills the rows below.
 *  - Fill handle (like Excel): drag the small square at the corner of a cell down to copy its
 *    value to the rows below (Taka No. counts up). Double-click it to fill down to the last taka.
 *    Ctrl+D copies the value from the row above.
 */
const COLUMNS = ['taka', 'meter', 'saree', 'design'];
const INITIAL_ROWS = 10;

let uid = 0;
const nextId = () => `r${(uid += 1)}`;

const isNumeric = (v) => /^\d+$/.test(String(v || '').trim());
const hasMeter = (r) => String(r.meter).trim() !== '';

/** A new row after `prev`: next Taka No., same Saree and Design. */
function rowAfter(prev) {
  return {
    id: nextId(),
    taka: prev && isNumeric(prev.taka) ? String(Number(prev.taka) + 1) : prev ? '' : '1',
    meter: '',
    saree: prev ? prev.saree : '',
    design: prev ? prev.design : '',
  };
}

function makeRows(count, after = null) {
  const rows = [];
  let prev = after;
  for (let i = 0; i < count; i += 1) {
    prev = rowAfter(prev);
    rows.push(prev);
  }
  return rows;
}

function cleanCell(field, value) {
  const v = String(value ?? '');
  if (field === 'meter') return v.replace(/[^0-9.]/g, '');
  if (field === 'saree') return v.replace(/\D/g, '');
  if (field === 'taka') return v.replace(/\s/g, '');
  return v.toUpperCase();
}

const sumRows = (rows) => {
  const filled = rows.filter(hasMeter);
  return {
    taka: filled.length,
    meter: filled.reduce((s, r) => s + (Number(r.meter) || 0), 0),
    saree: filled.reduce((s, r) => s + (parseInt(r.saree, 10) || 0), 0),
  };
};

const initialCopies = () =>
  Object.fromEntries(CHALLAN_PARTY_TYPES.map((t) => [t.key, { checked: false, partyId: '', gstNo: '', brokerName: '' }]));

/** Same look as the numbered steps on the order form (OrderDetail → OrderFormStepGroup). */
function SectionCard({ step, title, extra, children }) {
  return (
    <section
      className="rounded-2xl border border-slate-200/90 bg-gradient-to-b from-slate-50/90 to-white p-4 shadow-sm sm:p-5"
      aria-labelledby={`challan-step-${step}-title`}
    >
      <header className="mb-4 flex flex-wrap items-center gap-3 border-b border-slate-200/80 pb-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-black text-white shadow-sm ring-2 ring-primary-500/25"
          aria-hidden
        >
          {step}
        </span>
        <h2 id={`challan-step-${step}-title`} className="min-w-0 flex-1 text-base font-extrabold tracking-tight text-slate-900 sm:text-lg">
          {title}
        </h2>
        {extra}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function ChallanForm({ challanId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const isEdit = Boolean(challanId);
  const canSave = isEdit ? can('CHALLANS', 'EDIT') : can('CHALLANS', 'CREATE');
  const readOnly = !canSave;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** Only for "could not load" (the page cannot be used). Everything else is a toast. */
  const [loadError, setLoadError] = useState('');
  // One message at a time, top-right (same toast as the rest of the app), visible wherever the user has scrolled.
  const toast = useToast();
  const lastToast = useRef(null);
  const notify = (type, text) => {
    if (lastToast.current) toast.dismiss(lastToast.current);
    lastToast.current = toast[type](text, { duration: type === 'error' ? 7000 : 3500 });
  };
  const setFormError = (text) => {
    if (text) notify('error', text);
    else if (lastToast.current) { toast.dismiss(lastToast.current); lastToast.current = null; }
  };
  const setMessage = (text) => { if (text) notify('success', text); };

  const [saved, setSaved] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [nextNo, setNextNo] = useState(null);
  const [challanDate, setChallanDate] = useState(todayIso);
  const [quality, setQuality] = useState('');
  const [khataId, setKhataId] = useState('');
  const [copies, setCopies] = useState(initialCopies);
  const [rows, setRows] = useState(() => makeRows(INITIAL_ROWS));

  // bulk update
  const [range, setRange] = useState({ from: '', to: '' });
  const [bulk, setBulk] = useState({ design: '', saree: '' });
  /** Cell with the fill handle (small square, like Excel), and the rows being dragged over. */
  const [activeCell, setActiveCell] = useState(null); // { id, field }
  const [fillDrag, setFillDrag] = useState(null); // { field, from, to }
  /** Cells that failed the check on Save, e.g. { 'meter-r3': true } (shown in red). */
  const [cellErrors, setCellErrors] = useState({});
  const anchorRow = useRef(null);

  const [partiesByType, setPartiesByType] = useState({});
  const [khatas, setKhatas] = useState([]);

  const refs = useRef({});
  const pendingFocus = useRef(null);
  const bodyScrollRef = useRef(null);

  // ---------- load lists (+ the challan when editing) ----------
  useEffect(() => {
    let cancelled = false;
    const optional = (p) => p.then((r) => r.data).catch(() => null);
    (async () => {
      setLoading(true);
      try {
        const [partyLists, khataList, challan] = await Promise.all([
          Promise.all(CHALLAN_PARTY_TYPES.map((t) => optional(api.get(`/api/parties?category=${t.key}`)))),
          optional(api.get('/api/khatas')),
          isEdit ? api.get(`/api/challans/${challanId}`).then((r) => r.data) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const byType = {};
        CHALLAN_PARTY_TYPES.forEach((t, i) => {
          byType[t.key] = Array.isArray(partyLists[i]) ? partyLists[i] : [];
        });
        setPartiesByType(byType);
        setKhatas(Array.isArray(khataList) ? khataList : []);
        if (challan) {
          setSaved(challan);
          setChallanDate(challan.challanDate || todayIso());
          setQuality(challan.quality || '');
          setKhataId(challan.khataId || '');
          const nextCopies = initialCopies();
          (challan.parties || []).forEach((p) => {
            if (nextCopies[p.category]) {
              nextCopies[p.category] = {
                checked: true,
                partyId: p.partyId,
                gstNo: p.gstNo || '',
                brokerName: p.brokerName || '',
                savedName: p.partyName,
              };
            }
          });
          setCopies(nextCopies);
          const loaded = (challan.lines || []).map((l) => ({
            id: nextId(),
            taka: String(l.takaNo ?? ''),
            meter: l.meters == null ? '' : formatMeters(l.meters),
            saree: String(l.saree ?? ''),
            design: String(l.designNo || '').toUpperCase(),
          }));
          setRows(loaded.length ? loaded : makeRows(INITIAL_ROWS));
        }
      } catch (err) {
        if (!cancelled) setLoadError(extractErrorMessage(err, 'Unable to load challan'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [challanId, isEdit]);

  // New challan: show the number it will get (follows the financial year of the selected date).
  useEffect(() => {
    if (isEdit || !challanDate) return undefined;
    let cancelled = false;
    api.get(`/api/challans/next-number?date=${challanDate}`)
      .then((r) => { if (!cancelled) setNextNo(r.data?.nextChallanNo ?? null); })
      .catch(() => { if (!cancelled) setNextNo(null); });
    return () => { cancelled = true; };
  }, [isEdit, challanDate]);

  // Message passed from the "new challan" page after saving.
  const flash = location.state?.flash;
  useEffect(() => {
    if (flash) toast.success(flash, { duration: 3500 });
  }, [flash, toast]);

  // Focus requested by keyboard actions, after the rows have re-rendered.
  useEffect(() => {
    if (!pendingFocus.current) return;
    const key = pendingFocus.current;
    pendingFocus.current = null;
    const el = refs.current[key];
    if (el) {
      el.focus();
      if (el.select) el.select();
    }
  });

  // ---------- table ----------
  const totals = useMemo(() => sumRows(rows), [rows]);
  const lastFilledIndex = useMemo(() => rows.map(hasMeter).lastIndexOf(true), [rows]);
  const rangeFrom = parseInt(range.from, 10);
  const rangeTo = parseInt(range.to, 10);
  const inRange = (i) => rangeFrom >= 1 && rangeTo >= rangeFrom && i + 1 >= rangeFrom && i + 1 <= rangeTo;
  const full = rows.length >= MAX_CHALLAN_ROWS;

  const setCell = (i, field, value) => {
    const v = cleanCell(field, value);
    const key = `${field}-${rows[i]?.id}`;
    if (cellErrors[key]) setCellErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
    setRows((prev) => prev.map((r, k) => (k === i ? { ...r, [field]: v } : r)));
  };

  const addRows = (count, focusField = null) => {
    const n = Math.max(0, Math.min(Number(count) || 0, MAX_CHALLAN_ROWS - rows.length));
    if (!n) {
      setFormError(`A challan can have at most ${MAX_CHALLAN_ROWS} rows.`);
      return;
    }
    const added = makeRows(n, rows[rows.length - 1] || null);
    setRows((prev) => [...prev, ...added]);
    if (focusField) pendingFocus.current = `${focusField}-${added[0].id}`;
  };

  const deleteRow = (i) => setRows((prev) => (prev.length > 1 ? prev.filter((_, k) => k !== i) : makeRows(1)));

  /** Removes every row without a meter (they are skipped on save anyway). */
  const removeEmptyRows = () => {
    setCellErrors({});
    setRows((prev) => {
      const kept = prev.filter(hasMeter);
      return kept.length ? kept : makeRows(1);
    });
  };

  /** Click on Sr.: choose a row; Shift+click: choose up to that row. */
  const onSrClick = (e, i) => {
    if (readOnly) return;
    if (e.shiftKey && anchorRow.current != null) {
      const a = Math.min(anchorRow.current, i) + 1;
      const b = Math.max(anchorRow.current, i) + 1;
      setRange({ from: String(a), to: String(b) });
    } else {
      anchorRow.current = i;
      setRange({ from: String(i + 1), to: String(i + 1) });
    }
  };

  const applyBulk = () => {
    setFormError('');
    const from = parseInt(range.from, 10);
    const to = parseInt(range.to, 10);
    if (!(from >= 1) || !(to >= from)) {
      setFormError('Bulk update: enter the rows, e.g. from 1 to 10.');
      return;
    }
    const design = bulk.design.trim().toUpperCase();
    const saree = bulk.saree.trim();
    if (!design && !saree) {
      setFormError('Bulk update: type a Design No. or Saree to set.');
      return;
    }
    if (to > MAX_CHALLAN_ROWS) {
      setFormError(`A challan can have at most ${MAX_CHALLAN_ROWS} rows.`);
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      while (next.length < to) next.push(rowAfter(next[next.length - 1]));
      for (let i = from - 1; i < to; i += 1) {
        const r = { ...next[i] };
        if (design) r.design = design;
        if (saree) r.saree = saree;
        next[i] = r;
      }
      return next;
    });
    const what = [design && `Design ${design}`, saree && `Saree ${saree}`].filter(Boolean).join(', ');
    setMessage(`Rows ${from}–${to} updated: ${what}.`);
    // The next block usually starts right after this one.
    setRange({ from: String(to + 1), to: '' });
    setBulk((b) => ({ ...b, design: '' }));
  };

  /** Copies rows[from][field] to rows from+1..to (Taka No. counts up instead of copying). */
  const fillDown = (field, from, to) => {
    if (to <= from) return;
    const source = rows[from] ? String(rows[from][field]).trim() : '';
    if (!source) {
      setFormError('Type a value in the cell first, then drag its small square down.');
      return;
    }
    const series = field === 'taka' && isNumeric(source);
    setRows((prev) => prev.map((r, k) => {
      if (k <= from || k > to) return r;
      return { ...r, [field]: series ? String(Number(source) + (k - from)) : source };
    }));
    const label = { taka: 'Taka No.', meter: 'Meter', saree: 'Saree', design: 'Design' }[field];
    setMessage(series
      ? `Taka No. filled ${Number(source) + 1}–${Number(source) + (to - from)} in rows ${from + 2}–${to + 1}.`
      : `${label} ${source} copied to rows ${from + 2}–${to + 1}.`);
  };

  const rowIndexAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const tr = el && el.closest ? el.closest('tr[data-row]') : null;
    return tr ? Number(tr.getAttribute('data-row')) : null;
  };

  const onFillPointerDown = (e, i, field) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    setFillDrag({ field, from: i, to: i });
  };

  const onFillPointerMove = (e) => {
    if (!fillDrag) return;
    // keep the page scrolling while dragging near the top / bottom edge
    const scroller = bodyScrollRef.current;
    if (scroller) {
      const rect = scroller.getBoundingClientRect();
      if (e.clientY > rect.bottom - 60) scroller.scrollTop += 18;
      else if (e.clientY < rect.top + 60) scroller.scrollTop -= 18;
    }
    const idx = rowIndexAt(e.clientX, e.clientY);
    if (idx == null) return;
    const to = Math.max(fillDrag.from, idx);
    if (to !== fillDrag.to) setFillDrag((d) => (d ? { ...d, to } : d));
  };

  const onFillPointerUp = () => {
    if (!fillDrag) return;
    const { field, from, to } = fillDrag;
    setFillDrag(null);
    fillDown(field, from, to);
  };

  /** Double-click on the fill handle: fill down to the last row that has a meter (or to the end). */
  const onFillDoubleClick = (e, i, field) => {
    e.preventDefault();
    const target = lastFilledIndex > i ? lastFilledIndex : rows.length - 1;
    fillDown(field, i, target);
  };

  const onCellKeyDown = (e, i, field) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
      // Ctrl+D (like Excel): copy the value from the row above
      e.preventDefault();
      if (i > 0) {
        const above = String(rows[i - 1][field]).trim();
        const v = field === 'taka' && isNumeric(above) ? String(Number(above) + 1) : above;
        setRows((prev) => prev.map((r, k) => (k === i ? { ...r, [field]: v } : r)));
      }
      return;
    }
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (i + 1 < rows.length) {
        pendingFocus.current = `${field}-${rows[i + 1].id}`;
        setRows((prev) => [...prev]);
      } else if (e.key === 'Enter' && !full) {
        addRows(1, field);
      }
    } else if (e.key === 'ArrowUp' && i > 0) {
      e.preventDefault();
      pendingFocus.current = `${field}-${rows[i - 1].id}`;
      setRows((prev) => [...prev]);
    }
  };

  /** Pasting several lines (and/or Tab-separated columns) from Excel / WhatsApp fills the rows below. */
  const onCellPaste = (e, i, field) => {
    const text = e.clipboardData?.getData('text') || '';
    const lines = text.replace(/\r/g, '').split('\n');
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    if (lines.length < 2 && !text.includes('\t')) return; // normal single-value paste
    e.preventDefault();
    const startCol = COLUMNS.indexOf(field);
    const room = MAX_CHALLAN_ROWS - i;
    const used = lines.slice(0, room);
    setRows((prev) => {
      const next = [...prev];
      used.forEach((line, k) => {
        const idx = i + k;
        while (next.length <= idx) next.push(rowAfter(next[next.length - 1]));
        const r = { ...next[idx] };
        line.split('\t').forEach((cellText, c) => {
          const col = COLUMNS[startCol + c];
          if (col) r[col] = cleanCell(col, cellText.trim());
        });
        next[idx] = r;
      });
      return next;
    });
    setMessage(`${used.length} rows pasted${lines.length > used.length ? ` (only ${used.length} fitted; max ${MAX_CHALLAN_ROWS} rows)` : ''}.`);
  };

  // ---------- parties ----------
  const toggleType = (key, checked) => {
    setCopies((prev) => {
      const cur = prev[key];
      let next = { ...cur, checked };
      if (checked && !cur.partyId) {
        const list = partiesByType[key] || [];
        if (list.length === 1) {
          next = { ...next, partyId: list[0].id, gstNo: list[0].gstNo || '', brokerName: list[0].brokerName || '' };
        }
      }
      return { ...prev, [key]: next };
    });
  };

  const selectParty = (key, partyId) => {
    const party = (partiesByType[key] || []).find((p) => String(p.id) === String(partyId));
    setCopies((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        partyId: partyId || '',
        // GST No and Broker come from the party; they can still be typed when the party has none.
        gstNo: party ? party.gstNo || '' : '',
        brokerName: party ? party.brokerName || '' : '',
      },
    }));
  };

  const setCopyField = (key, field, value) =>
    setCopies((prev) => ({ ...prev, [key]: { ...prev[key], [field]: field === 'gstNo' ? value.toUpperCase() : value } }));

  const checkedTypes = CHALLAN_PARTY_TYPES.filter((t) => copies[t.key].checked);

  // ---------- save ----------
  /** Header / party checks (message only). */
  const validateDetails = () => {
    if (!challanDate) return 'Please select the challan date.';
    if (!checkedTypes.length) return 'Tick at least one party type (Butta Cutting, Mill Finish, Border Cutting or Handwork).';
    for (const t of checkedTypes) {
      const c = copies[t.key];
      if (!c.partyId) return `Please select the ${t.label} party (M/s).`;
      if (!c.brokerName.trim()) return `Please enter Broker/Master for the ${t.label} party.`;
      const gst = normalizeGstNo(c.gstNo);
      if (gst && !GSTIN_PATTERN.test(gst)) return `GST No of the ${t.label} party is not valid (15 characters, e.g. 24ABCDE1234F1Z5).`;
    }
    return '';
  };

  /**
   * Taka table checks. Rows without a meter are empty and are skipped (wherever they are);
   * every row with a meter needs Saree, Design and Taka No.
   * Returns the first message, all wrong cells (shown in red) and the first cell to jump to.
   */
  const validateTable = () => {
    const cells = {};
    const messages = [];
    if (lastFilledIndex < 0) {
      const first = rows[0];
      if (first) {
        cells[`meter-${first.id}`] = true;
        if (!isNumeric(first.saree)) cells[`saree-${first.id}`] = true;
      }
      return { message: 'Enter at least one taka: Meter and Saree are required.', cells, focusKey: first ? `meter-${first.id}` : null };
    }
    const seen = new Map();
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (!hasMeter(r)) continue; // empty row: ignored
      const sr = i + 1;
      const add = (field, msg) => {
        cells[`${field}-${r.id}`] = true;
        messages.push({ key: `${field}-${r.id}`, msg });
      };
      if (!(Number(r.meter) > 0)) add('meter', `Row ${sr}: meter must be more than 0.`);
      if (!isNumeric(r.saree)) add('saree', `Row ${sr}: please enter the saree.`);
      if (!String(r.design).trim()) add('design', `Row ${sr}: please enter the design no.`);
      const taka = String(r.taka).trim();
      if (!taka) add('taka', `Row ${sr}: please enter the taka no.`);
      else if (seen.has(taka.toUpperCase())) add('taka', `Taka No. ${taka} is entered more than once (rows ${seen.get(taka.toUpperCase())} and ${sr}).`);
      else seen.set(taka.toUpperCase(), sr);
    }
    if (!messages.length) return { message: '', cells: {}, focusKey: null };
    const extra = messages.length > 1 ? ` ${messages.length} cells are marked in red.` : '';
    return { message: messages[0].msg + extra, cells, focusKey: messages[0].key };
  };

  const buildPayload = () => ({
    challanDate,
    quality: quality.trim() || null,
    khataId: khataId || null,
    parties: checkedTypes.map((t) => ({
      category: t.key,
      partyId: copies[t.key].partyId,
      gstNo: normalizeGstNo(copies[t.key].gstNo) || null,
      brokerName: copies[t.key].brokerName.trim() || null,
    })),
    lines: rows.filter(hasMeter).map((r) => ({
      takaNo: String(r.taka).trim(),
      meters: Number(r.meter),
      saree: parseInt(r.saree, 10),
      designNo: String(r.design).trim().toUpperCase(),
    })),
  });

  const save = async (download) => {
    setFormError('');
    setMessage('');
    const table = validateTable();
    setCellErrors(table.cells);
    const detailsError = validateDetails();
    if (detailsError || table.message) {
      setFormError(detailsError || table.message);
      if (!detailsError && table.focusKey && refs.current[table.focusKey]) {
        refs.current[table.focusKey].focus();
        refs.current[table.focusKey].scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else {
        bodyScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = isEdit
        ? await api.put(`/api/challans/${challanId}`, payload)
        : await api.post('/api/challans', payload);
      let flash = isEdit ? `Challan No. ${data.challanNo} updated.` : `Challan No. ${data.challanNo} saved.`;
      if (download) {
        try {
          await downloadChallanPdf(data);
        } catch (e) {
          flash += ` PDF download failed: ${e?.message || 'please try again'}.`;
        }
      }
      if (isEdit) {
        setSaved(data);
        setMessage(flash);
      } else {
        navigate(challanEditPath(data.id), { replace: true, state: { flash } });
      }
    } catch (e) {
      setFormError(extractErrorMessage(e, 'Unable to save challan'));
    } finally {
      setSaving(false);
    }
  };

  const downloadSaved = async (copy) => {
    if (!saved) return;
    setFormError('');
    try {
      await downloadChallanPdf(saved, copy);
    } catch (e) {
      setFormError(e?.message || 'Unable to download PDF');
    }
  };

  // ---------- render ----------
  const khataOptions = khatas.map((k) => ({ value: k.id, label: k.name }));
  if (khataId && !khataOptions.some((o) => String(o.value) === String(khataId)) && saved?.manufacturedBy) {
    khataOptions.push({ value: khataId, label: saved.manufacturedBy });
  }

  const cell = (r, i, field, { align = 'left' } = {}) => (
    <input
      ref={(el) => { refs.current[`${field}-${r.id}`] = el; }}
      className={`h-9 w-full border-0 bg-transparent px-2 text-sm tabular-nums text-slate-900 outline-none focus:bg-white focus:ring-2 focus:ring-inset focus:ring-primary-500 ${align === 'right' ? 'text-right' : ''} ${field === 'design' ? 'uppercase' : ''}`}
      value={r[field]}
      inputMode={field === 'meter' ? 'decimal' : field === 'saree' ? 'numeric' : undefined}
      disabled={readOnly}
      onFocus={(e) => { e.target.select(); setActiveCell({ id: r.id, field }); }}
      onChange={(e) => setCell(i, field, e.target.value)}
      onKeyDown={(e) => onCellKeyDown(e, i, field)}
      onPaste={(e) => onCellPaste(e, i, field)}
      aria-label={`Row ${i + 1} ${field === 'taka' ? 'taka no.' : field === 'design' ? 'design no.' : field}`}
    />
  );

  /** Table cell with the Excel-like fill handle on the active cell. */
  const editableTd = (r, i, field, opts) => {
    const active = !readOnly && activeCell && activeCell.id === r.id && activeCell.field === field;
    const filling = fillDrag && fillDrag.field === field && i > fillDrag.from && i <= fillDrag.to;
    return (
      <td className={`relative border-b border-r border-slate-200 p-0 ${cellErrors[`${field}-${r.id}`] ? 'bg-red-50 outline outline-2 -outline-offset-2 outline-red-500' : ''} ${filling ? 'bg-primary-100 outline-dashed outline-2 -outline-offset-2 outline-primary-500' : ''} ${active ? 'outline outline-2 -outline-offset-2 outline-primary-500' : ''}`}>
        {cell(r, i, field, opts)}
        {active ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Drag down to copy this value to the rows below"
            title="Drag down to copy to the rows below. Double-click: fill down to the last taka."
            className="absolute -bottom-1.5 -right-1.5 z-10 h-3 w-3 cursor-crosshair touch-none rounded-sm border-2 border-white bg-primary-600 shadow"
            onPointerDown={(e) => onFillPointerDown(e, i, field)}
            onPointerMove={onFillPointerMove}
            onPointerUp={onFillPointerUp}
            onPointerCancel={() => setFillDrag(null)}
            onDoubleClick={(e) => onFillDoubleClick(e, i, field)}
          />
        ) : null}
      </td>
    );
  };

  const remaining = MAX_CHALLAN_ROWS - rows.length;
  const usedPct = Math.min(100, Math.round((totals.taka / MAX_CHALLAN_ROWS) * 100));

  const title = isEdit
    ? `Challan No. ${saved?.challanNo ?? ''}${saved?.financialYear ? ` · FY ${saved.financialYear}` : ''}`
    : 'New Delivery Challan';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Top bar: same layout and buttons as the order page */}
      <div className="shrink-0 overflow-visible border-b border-slate-200 bg-white shadow-sm">
        <div className="flex w-full flex-wrap items-center justify-between gap-3 py-3.5 pl-2 pr-2 sm:pl-3 sm:pr-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => navigate(challansListPath())}
              leftIcon={(
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              )}
            >
              Back to challans
            </Button>
            <h1 className="truncate text-base font-extrabold tracking-tight text-slate-900">{title}</h1>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {isEdit && saved && (saved.parties || []).length > 1 ? saved.parties.map((p) => (
              <Button key={p.category} type="button" variant="outline" size="sm" onClick={() => downloadSaved(p.category)}>
                PDF: {CHALLAN_PARTY_TYPES.find((t) => t.key === p.category)?.short || p.category}
              </Button>
            )) : null}
            {isEdit && saved ? (
              <>
                <Button type="button" variant="secondary" size="sm" onClick={() => setPreviewOpen(true)}>Preview</Button>
                <Button type="button" variant="secondary" size="sm" onClick={() => downloadSaved()}>Download PDF</Button>
              </>
            ) : null}
            {!readOnly && !loading && !loadError ? (
              <>
                <Button type="button" variant="outline" size="sm" onClick={() => save(false)} disabled={saving}>
                  {isEdit ? 'Update' : 'Save'}
                </Button>
                <Button type="button" variant="primary" size="sm" onClick={() => save(true)} loading={saving}>
                  {isEdit ? 'Update & Download PDF' : 'Save & Download PDF'}
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div ref={bodyScrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-y-auto">
      <div className="space-y-6 pb-6 pt-4 sm:pt-5">
      <ModuleAccessBanner moduleKey="CHALLANS" className="mb-0" />
      <AlertMessage message={loadError} type="error" />

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : loadError ? null : (
        <>
          <SectionCard step="1" title="Challan details">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                label="Challan No."
                value={String((isEdit ? saved?.challanNo : nextNo) ?? '')}
                readOnly
                tabIndex={-1}
                className="cursor-default bg-slate-50 font-bold text-slate-900"
              />
              <Input label="Date *" type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)} disabled={readOnly} />
              <Input label="Quality" value={quality} maxLength={200} onChange={(e) => setQuality(e.target.value)} disabled={readOnly} />
              <Select label="Manufactured By" value={khataId} onChange={(e) => setKhataId(e.target.value)} disabled={readOnly}>
                <option value="">Select</option>
                {khataOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </div>
          </SectionCard>

          <SectionCard
            step="2"
            title="Send to"
            extra={(
              <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-primary-700">
                {checkedTypes.length ? `${checkedTypes.length} cop${checkedTypes.length === 1 ? 'y' : 'ies'} in PDF` : 'tick at least one'}
              </span>
            )}
          >
            <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
              {CHALLAN_PARTY_TYPES.map((t) => {
                const on = copies[t.key].checked;
                return (
                  <label
                    key={t.key}
                    className={`flex cursor-pointer select-none items-center gap-2.5 rounded-xl border-2 px-3 py-2.5 ${on ? 'border-primary-500 bg-primary-50' : 'border-slate-200 bg-white'} ${readOnly ? 'cursor-default opacity-80' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary-600"
                      checked={on}
                      disabled={readOnly}
                      onChange={(e) => toggleType(t.key, e.target.checked)}
                    />
                    <span>
                      <span className="block text-sm font-semibold text-slate-800">{t.label}</span>
                      <span className="block text-[11px] text-slate-500">{(partiesByType[t.key] || []).length} parties</span>
                    </span>
                  </label>
                );
              })}
            </div>

            {checkedTypes.length ? (
              <div className="mt-3 space-y-2.5">
                {checkedTypes.map((t) => {
                  const c = copies[t.key];
                  const list = partiesByType[t.key] || [];
                  const options = list.map((p) => ({ value: p.id, label: p.name }));
                  if (c.partyId && !options.some((o) => String(o.value) === String(c.partyId))) {
                    options.push({ value: c.partyId, label: c.savedName || 'Saved party' });
                  }
                  return (
                    <div key={t.key} className="rounded-xl border border-dashed border-primary-200 bg-white p-3">
                      <div className="mb-2 text-sm font-bold text-primary-700">{t.label}</div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <SearchableSelect
                          label="M/s. *"
                          placeholder="Select"
                          options={options}
                          value={c.partyId}
                          onChange={(id) => selectParty(t.key, id)}
                          disabled={readOnly}
                          emptyMessage="No party starts with that text"
                        />
                        <Input
                          label="Broker / Master *"
                          value={c.brokerName}
                          maxLength={120}
                          onChange={(e) => setCopyField(t.key, 'brokerName', e.target.value)}
                          disabled={readOnly}
                        />
                        <Input
                          label="GST No."
                          value={c.gstNo}
                          maxLength={15}
                          onChange={(e) => setCopyField(t.key, 'gstNo', e.target.value)}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </SectionCard>

          <SectionCard
            step="3"
            title="Takas"
            extra={(
              <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-primary-700">
                {rows.length} / {MAX_CHALLAN_ROWS} rows
              </span>
            )}
          >
            {!readOnly ? (
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Bulk update</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-24">
                    <Input label="Row from" inputMode="numeric" value={range.from}
                      onChange={(e) => setRange((r) => ({ ...r, from: e.target.value.replace(/\D/g, '') }))} />
                  </div>
                  <div className="w-24">
                    <Input label="Row to" inputMode="numeric" value={range.to}
                      onChange={(e) => setRange((r) => ({ ...r, to: e.target.value.replace(/\D/g, '') }))} />
                  </div>
                  <div className="w-52">
                    <Input label="Design No." className="uppercase" value={bulk.design}
                      onChange={(e) => setBulk((b) => ({ ...b, design: e.target.value.toUpperCase() }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyBulk(); } }} />
                  </div>
                  <div className="w-28">
                    <Input label="Saree" inputMode="numeric" value={bulk.saree}
                      onChange={(e) => setBulk((b) => ({ ...b, saree: e.target.value.replace(/\D/g, '') }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyBulk(); } }} />
                  </div>
                  <Button type="button" variant="primary" onClick={applyBulk}>Apply to rows</Button>
                </div>
              </div>
            ) : null}

            <div className={`overflow-x-auto border border-slate-300 ${readOnly ? 'rounded-xl' : 'rounded-t-xl'}`}>
              <table className="w-full min-w-[560px] table-fixed border-collapse">
                <colgroup>
                  <col style={{ width: 56 }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '14%' }} />
                  <col />
                  <col style={{ width: 40 }} />
                </colgroup>
                <thead>
                  <tr className="bg-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                    <th className="border-b border-r border-slate-300 px-2 py-2 text-center">Sr.</th>
                    <th className="border-b border-r border-slate-300 px-2 py-2">Taka No.</th>
                    <th className="border-b border-r border-slate-300 px-2 py-2 text-right">Meter</th>
                    <th className="border-b border-r border-slate-300 px-2 py-2 text-right">Saree</th>
                    <th className="border-b border-r border-slate-300 px-2 py-2">Design No.</th>
                    <th className="border-b border-slate-300" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const selected = inRange(i);
                    const designChanged = i > 0 && r.design && rows[i - 1].design && r.design !== rows[i - 1].design;
                    return (
                      <tr
                        key={r.id}
                        data-row={i}
                        className={`${selected ? 'bg-primary-50' : i % 2 ? 'bg-slate-50' : 'bg-white'} ${designChanged ? 'border-t-2 border-t-primary-400' : ''}`}
                      >
                        <td
                          className={`select-none border-b border-r border-slate-200 text-center text-sm tabular-nums ${readOnly ? '' : 'cursor-pointer hover:bg-primary-100'} ${selected ? 'font-bold text-primary-700' : hasMeter(r) ? 'text-slate-600' : 'text-slate-300'}`}
                          onClick={(e) => onSrClick(e, i)}
                          title={readOnly ? undefined : 'Click to choose this row for Bulk update (Shift+click to choose many)'}
                        >
                          {i + 1}
                        </td>
                        {editableTd(r, i, 'taka')}
                        {editableTd(r, i, 'meter', { align: 'right' })}
                        {editableTd(r, i, 'saree', { align: 'right' })}
                        {editableTd(r, i, 'design')}
                        <td className="border-b border-slate-200 text-center">
                          {!readOnly ? (
                            <button type="button" onClick={() => deleteRow(i)} className="text-lg leading-none text-slate-300 hover:text-red-600" title="Delete row" aria-label={`Delete row ${i + 1}`}>×</button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 text-sm font-bold tabular-nums text-slate-800">
                    <td className="border-t border-r border-slate-300 px-2 py-2 text-center text-xs uppercase text-slate-500">Total</td>
                    <td className="border-t border-r border-slate-300 px-2 py-2">{totals.taka} taka</td>
                    <td className="border-t border-r border-slate-300 px-2 py-2 text-right">{formatMeters(totals.meter)}</td>
                    <td className="border-t border-r border-slate-300 px-2 py-2 text-right">{totals.saree}</td>
                    <td className="border-t border-slate-300" colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>

            {!readOnly ? (
              <div className="rounded-b-xl border border-t-0 border-slate-300 bg-slate-50 px-3 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addRows(1, 'meter')}
                    disabled={full}
                    leftIcon={(
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                  >
                    Add row
                  </Button>
                  {[5, 10, 15].map((n) => (
                    <Button key={n} type="button" variant="secondary" size="sm" onClick={() => addRows(n)} disabled={remaining < 1}>
                      +{Math.min(n, Math.max(remaining, 0)) || n} rows
                    </Button>
                  ))}
                  {rows.length > totals.taka ? (
                    <Button type="button" variant="ghost" size="sm" onClick={removeEmptyRows}>Remove empty rows</Button>
                  ) : null}
                  <div className="ml-auto flex min-w-[170px] items-center gap-2">
                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
                      <div className={`h-full rounded-full ${full ? 'bg-amber-500' : 'bg-primary-500'}`} style={{ width: `${usedPct}%` }} />
                    </div>
                    <span className="text-xs tabular-nums text-slate-500">
                      {totals.taka} takas · {rows.length}/{MAX_CHALLAN_ROWS} rows
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </SectionCard>

        </>
      )}
      </div>
      </div>

      <ChallanPreviewModal open={previewOpen} challan={saved} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
